import React, { Component } from "react";
import styled from "styled-components/macro";
import * as Sentry from "@sentry/react";
import { reloadPage } from "../utils/navigationUtils";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--juno-space-8);
  font-family: var(--juno-font-family-body);
  background-color: var(--juno-color-page);
`;

const Card = styled.div`
  background-color: var(--juno-color-surface);
  border-radius: var(--juno-radius-lg);
  padding: var(--juno-space-8);
  max-width: 500px;
  width: 100%;
  box-shadow: var(--juno-shadow-dropdown);
  text-align: center;
`;

const Heading = styled.h1`
  margin: 0 0 var(--juno-space-4) 0;
  font-size: var(--juno-font-size-h2-md);
  color: var(--juno-color-text);
  font-weight: var(--juno-font-weight-bold);
`;

const Message = styled.p`
  margin: 0 0 var(--juno-space-6) 0;
  color: var(--juno-color-text-secondary);
  font-size: var(--juno-font-size-body-md);
  line-height: 1.5;
`;

const Button = styled.button`
  background-color: var(--juno-color-button);
  color: var(--juno-color-button-text);
  border: none;
  border-radius: var(--juno-radius-sm);
  padding: var(--juno-space-3) var(--juno-space-6);
  font-size: var(--juno-font-size-body-sm);
  font-family: var(--juno-font-family-body);
  font-weight: var(--juno-font-weight-bold);
  cursor: pointer;
  box-shadow: 0 2px 0 var(--juno-color-button-shadow);

  &:hover {
    background-color: var(--juno-color-button-hover);
  }

  &:focus-visible {
    outline: var(--juno-focus-outline-width) solid var(--juno-color-focus);
    outline-offset: var(--juno-focus-outline-offset);
  }
`;

function ErrorFallback() {
  const handleRefresh = () => {
    reloadPage();
  };

  return (
    <Container>
      <Card>
        <Heading>This page didn’t load</Heading>
        <Message>
          We’ve logged this so we can look into it. Try refreshing – you may need to re-enter any recent details.
        </Message>
        <Button onClick={handleRefresh}>Refresh</Button>
      </Card>
    </Container>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);

    try {
      Sentry.captureReactException(error, errorInfo, {
        mechanism: { handled: true },
        tags: { action: "ErrorBoundary" },
      });
    } catch (reportingError) {
      console.error("[ErrorBoundary] Failed to report to Sentry:", reportingError);
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
